import React, { useState, useEffect } from 'react'
import {
  Layout,
  Menu,
  Button,
  Avatar,
  Badge,
  Space,
  Drawer,
  theme,
  Dropdown,
  Switch,
  Tooltip,
  message,
  ConfigProvider,
  Flex,
  Typography,
} from 'antd'
import {
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  ShoppingCartOutlined,
  BarChartOutlined,
  UserOutlined,
  BellOutlined,
  SettingOutlined,
  LogoutOutlined,
  QrcodeOutlined,
  ControlOutlined,
  PlusOutlined,
  UserAddOutlined,
  SwapOutlined,
} from '@ant-design/icons'
import { ROUTE_PATHS } from '../../routes/constants'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { getAppState } from '../../redux/app/actions'
import { setApploading } from '../../redux/app/reducers'
import StoreQRCodeModal from '../../components/Modals/StoreQRCodeModal'
import QuickActionDrawer from './components/QuickActionDrawer'
import BrandLogo from '../../components/BrandLogo'
import useIsMobile from '../../hooks/useIsMobile'
import api from '../../utils/api'
import { setAuthTokens, logoutUser, refreshAuthTokens } from '../../utils/auth'
import { Store, getPricingPlanName, getPricingPlanColor } from '../../redux/app/types'
import { selectUser } from '../../redux/user/actions'
import { getModuleMenuItems } from './menuItems'
import { getStoreUrl } from '../../utils/store'
import './ModernSidebar.css'

const { Header, Sider, Content } = Layout
const { Text } = Typography

interface Props {
  children: React.ReactNode
}

const SidebarLayout: React.FC<Props> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false)
  const [isDarkTheme, setIsDarkTheme] = useState(false)
  const [qrModalOpen, setQrModalOpen] = useState(false)
  const [quickActionDrawerVisible, setQuickActionDrawerVisible] = useState(false)

  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { token } = theme.useToken()
  const isMobile = useIsMobile()

  const appState = useSelector(getAppState)
  const { selectedStore, stores, pricingPlan } = appState
  const [storeUrl, setStoreUrl] = useState<string>('')

  useEffect(() => {
    setStoreUrl(getStoreUrl(selectedStore?.store_domain))
  }, [selectedStore?.store_domain])
  const isStoreSelected = selectedStore?.id
  const profilePic = useSelector(selectUser)?.profile_pic_url

  useEffect(() => {
    if (isMobile) {
      setCollapsed(true)
    }
  }, [isMobile])

  const moduleMenuItems = getModuleMenuItems(
    selectedStore?.modules,
    typeof selectedStore?.role === 'string' ? selectedStore.role : '',
  )

  const handleLogout = async () => {
    try {
      await logoutUser()
      navigate(ROUTE_PATHS.HOME)
    } catch (error) {
      message.error('Logout failed. Please try again.')
    }
  }

  const handleStoreSwitch = async (store: Store) => {
    try {
      dispatch(setApploading(true))
      const response = await api.post('/auth/switch-store', {
        store: store.store_id,
      })

      setAuthTokens(response.access_token, response.refresh_token)

      await refreshAuthTokens()

      message.success(`Switched to ${store.store_domain}`)
      navigate(ROUTE_PATHS.DASHBOARD)
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Failed to switch store')
    } finally {
      dispatch(setApploading(false))
    }
  }

  const handleMenuClick = (path: string) => {
    navigate(path)
    if (isMobile) {
      setCollapsed(true)
    }
  }

  const handleCancel = () => {
    setQrModalOpen(false)
  }

  const hideDrawer = () => {
    setQuickActionDrawerVisible(false)
  }

  const profileMenu = (
    <Menu>
      <Menu.Item key="profile" icon={<UserOutlined />} onClick={() => navigate(ROUTE_PATHS.PROFILE)}>
        Profile
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="theme" icon={<Switch size="small" checked={isDarkTheme} onChange={setIsDarkTheme} />}>
        {!isDarkTheme ? 'Dark Mode' : 'Light Mode'}
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="logout" icon={<LogoutOutlined />} onClick={handleLogout}>
        Logout
      </Menu.Item>
    </Menu>
  )

  const convertToMenuItems = () => {
    return moduleMenuItems.map(route => ({
      key: route.path || route.key,
      icon: route.icon,
      label: (
        <span className="modern-menu-label">
          {route.label}
          <div className="modern-menu-indicator" />
        </span>
      ),
    }))
  }

  const handleMenuItemClick = ({ key }: { key: string }) => {
    const item = moduleMenuItems.find(m => m.path === key)
    if (item?.path) {
      handleMenuClick(item.path)
    }
  }

  // Enhanced store info card with store switching for modern sidebar
  const renderStoreCard = () => {
    if (collapsed) return null

    return (
      <div className="modern-store-card">
        <div className="store-card-header">
          <div className="store-avatar">{selectedStore?.store_domain?.charAt(0).toUpperCase() || 'S'}</div>
          <div className="store-info">
            <div className="store-name">{selectedStore?.store_domain || 'Store'}</div>
            <div className="store-status">
              <div className="status-indicator active" />
              <span>Active</span>
              {pricingPlan && (
                <div className="pricing-plan-badge" style={{ backgroundColor: getPricingPlanColor(pricingPlan) }}>
                  {getPricingPlanName(pricingPlan)}
                </div>
              )}
            </div>
          </div>
          {stores && stores.length > 1 && (
            <Dropdown
              overlay={
                <Menu className="store-switch-menu">
                  <Menu.ItemGroup title="Switch Store">
                    {stores.map(store => (
                      <Menu.Item
                        key={store.id}
                        onClick={() => handleStoreSwitch(store)}
                        disabled={store.id === selectedStore?.id}
                        className={store.id === selectedStore?.id ? 'current-store' : ''}
                      >
                        <Space align="center">
                          <Avatar size="small" className="store-menu-avatar">
                            {store.store_domain?.charAt(0).toUpperCase()}
                          </Avatar>
                          <div>
                            <div className="store-menu-name">{store.store_domain}</div>
                            {store.id === selectedStore?.id && <div className="store-menu-status">Current</div>}
                          </div>
                        </Space>
                      </Menu.Item>
                    ))}
                  </Menu.ItemGroup>
                </Menu>
              }
              trigger={['click']}
              placement="bottomRight"
            >
              <Button
                type="text"
                size="small"
                icon={<SwapOutlined />}
                className="store-switch-btn"
                title="Switch Store"
              />
            </Dropdown>
          )}
        </div>
        <div className="store-card-actions">
          <Button
            type="text"
            size="small"
            icon={<QrcodeOutlined />}
            className="store-action-btn"
            onClick={() => setQrModalOpen(true)}
            title="Store QR Code"
          >
            QR Code
          </Button>
          <Button
            type="text"
            size="small"
            icon={<SettingOutlined />}
            className="store-action-btn"
            onClick={() => navigate(ROUTE_PATHS.SETTINGS)}
            title="Store Settings"
          >
            Settings
          </Button>
        </div>
      </div>
    )
  }

  // Quick actions panel
  const renderQuickActions = () => {
    if (collapsed) return null

    return (
      <div className="modern-quick-actions">
        <div className="quick-actions-header">
          <Text className="quick-actions-title">Quick Actions</Text>
        </div>
        <div className="quick-actions-grid">
          <Button
            type="text"
            className="quick-action-item"
            icon={<PlusOutlined />}
            onClick={() => navigate(ROUTE_PATHS.PRODUCTS)}
          >
            Add Product
          </Button>
          <Button
            type="text"
            className="quick-action-item"
            icon={<ShoppingCartOutlined />}
            onClick={() => navigate(ROUTE_PATHS.ORDERS)}
          >
            View Orders
          </Button>
          <Button
            type="text"
            className="quick-action-item"
            icon={<UserAddOutlined />}
            onClick={() => navigate(ROUTE_PATHS.CUSTOMERS)}
          >
            Add Customer
          </Button>
          <Button
            type="text"
            className="quick-action-item"
            icon={<BarChartOutlined />}
            onClick={() => navigate(ROUTE_PATHS.REPORTS_SALES)}
          >
            Analytics
          </Button>
        </div>
      </div>
    )
  }

  const sidebarContent = (
    <div className={`modern-sidebar-container ${collapsed ? 'modern-sidebar-collapsed' : ''}`}>
      {/* Fixed Header */}
      {!isMobile && (
        <div className="modern-sidebar-header">
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            className="modern-collapse-btn"
            style={{
              width: collapsed ? '48px' : '40px',
              height: collapsed ? '48px' : '40px',
              minWidth: collapsed ? '48px' : '40px',
              flexShrink: 0,
            }}
          />
          {!collapsed && (
            <div className="modern-logo-container">
              <BrandLogo collapsed={collapsed} />
            </div>
          )}
        </div>
      )}

      {/* Scrollable Content Area */}
      <div className="modern-sidebar-scrollable">
        {/* Store info card */}
        {!isMobile && !collapsed && isStoreSelected && renderStoreCard()}

        {/* Menu */}
        <div className="modern-menu-container">
          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            items={convertToMenuItems()}
            className={`modern-menu ${collapsed ? 'modern-menu-collapsed' : ''}`}
            onClick={handleMenuItemClick}
          />
        </div>

        {/* Quick Actions */}
        {!isMobile && !collapsed && isStoreSelected && renderQuickActions()}
      </div>
    </div>
  )

  return (
    <ConfigProvider
      theme={{
        algorithm: isDarkTheme ? [theme.darkAlgorithm] : [theme.defaultAlgorithm],
        token: {
          colorPrimary: '#B8131B',
        },
        components: {
          Menu: {
            itemBorderRadius: 8,
            colorItemBg: 'transparent',
            colorItemBgHover: 'rgba(184, 19, 27, 0.08)',
            colorItemBgSelected: 'rgba(184, 19, 27, 0.12)',
            colorItemTextSelected: '#B8131B',
            colorItemTextHover: '#B8131B',
          },
        },
      }}
    >
      <Layout style={{ minHeight: '100vh', scrollbarWidth: 'thin', scrollbarColor: 'unset' }}>
        {!isMobile && (
          <Sider
            trigger={null}
            collapsed={collapsed}
            theme={isDarkTheme ? 'dark' : 'light'}
            defaultCollapsed={isMobile}
            onCollapse={setCollapsed}
            width={280}
            collapsedWidth={80}
            collapsible
            className={`modern-sidebar ${isDarkTheme ? 'dark' : ''}`}
          >
            {sidebarContent}
          </Sider>
        )}

        <Layout
          style={{
            marginLeft: !isMobile ? (collapsed ? '80px' : '280px') : '0',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <Header
            className={`modern-header ${isDarkTheme ? 'dark' : ''}`}
            style={{
              padding: '0 16px',
              height: '64px',
              lineHeight: '64px',
              width: isMobile ? '100%' : `calc(100vw - ${collapsed ? 80 : 280}px)`,
              position: 'fixed',
              top: 0,
              right: 0,
              zIndex: 999,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: isMobile ? 'space-between' : collapsed ? 'space-between' : 'flex-end',
              background: isDarkTheme ? 'rgba(0, 21, 41, 0.8)' : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(20px)',
              borderBottom: isDarkTheme ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(5, 5, 5, 0.06)',
            }}
          >
            {isMobile && (
              <Flex align="center" className="mobile-header-left">
                <Button
                  type="text"
                  icon={<MenuUnfoldOutlined />}
                  onClick={() => setCollapsed(!collapsed)}
                  className="modern-header-btn mobile-menu-btn"
                />
                <div className="mobile-logo-container">
                  <BrandLogo collapsed={false} />
                </div>
              </Flex>
            )}

            {!isMobile && collapsed && (
              <Flex align="center" className="desktop-collapsed-header">
                <div className="collapsed-logo-container">
                  <BrandLogo collapsed={false} />
                </div>
              </Flex>
            )}

            <Flex align="center" gap={8} className="header-actions">
              <Tooltip title="Quick Actions">
                <Button
                  type="text"
                  icon={<ControlOutlined />}
                  onClick={() => setQuickActionDrawerVisible(true)}
                  className="modern-header-btn"
                />
              </Tooltip>

              <Badge count={0}>
                <Button
                  type="text"
                  icon={<BellOutlined />}
                  onClick={() => navigate(ROUTE_PATHS.NOTIFICATIONS)}
                  className="modern-header-btn"
                />
              </Badge>

              <Dropdown overlay={profileMenu} trigger={['click']}>
                <Avatar
                  style={{ cursor: 'pointer' }}
                  src={profilePic}
                  icon={<UserOutlined />}
                  className="modern-avatar"
                />
              </Dropdown>
            </Flex>
          </Header>

          <Content
            style={{
              margin: '80px 16px 16px',
              minHeight: 'calc(100vh - 80px)',
            }}
          >
            {children}
          </Content>
        </Layout>

        {isMobile && (
          <Drawer
            placement="left"
            closeIcon={null}
            title={<BrandLogo collapsed={false} />}
            onClose={() => setCollapsed(true)}
            open={!collapsed}
            width={280}
            className="modern-mobile-drawer"
            styles={{
              header: {
                padding: '16px 24px',
                borderBottom: `1px solid ${token.colorBorder}`,
              },
              body: {
                padding: '0',
              },
            }}
          >
            <div className="modern-sidebar-container mobile-drawer-container">
              {/* Scrollable Content Area for Mobile */}
              <div className="modern-sidebar-scrollable">
                {/* Store info card for mobile */}
                {isStoreSelected && renderStoreCard()}

                {/* Menu */}
                <div className="modern-menu-container">
                  <Menu
                    mode="inline"
                    selectedKeys={[location.pathname]}
                    items={convertToMenuItems()}
                    className="modern-menu"
                    onClick={handleMenuItemClick}
                  />
                </div>

                {/* Quick Actions for mobile */}
                {isStoreSelected && renderQuickActions()}
              </div>
            </div>
          </Drawer>
        )}

        <StoreQRCodeModal isModalVisible={qrModalOpen} setIsModalVisible={handleCancel} url={storeUrl} />

        <QuickActionDrawer isOpen={quickActionDrawerVisible} setOpen={hideDrawer} url={storeUrl} />
      </Layout>
    </ConfigProvider>
  )
}

export default SidebarLayout
